import styles from './styles.scss';
import { rm, NO_ELEMENT, scrollToTarget } from 'utils';
import { Button, LoaderButton } from 'components/Button';
import { useToggle } from 'hooks';
import { ChangeEvent, useEffect, useReducer, useRef } from 'react';
import {
  reducer,
  productChanged,
  getDamageOptions,
  damageChange,
  getPackageSlipEntryOptions,
  packageSlipEntryChange,
  getReceivedOptions,
  receivedChange,
  solutionChange,
  getSolutionRequestsOptions,
  isValid,
  shippingDamageInitialState
} from './ShippingDamageHelper';
import ExtendedInfo from 'views/CustomerPage/OrderHistory/ExtendedInfo';
import { DisplayMode } from 'views/CustomerPage/OrderHistory/OrderLine';
import {
  BlueSectionContainer,
  ErrorContainer,
  SubmitContainer,
  RadioBtnContainer
} from '../Components/SectionContainer/SectionContainer';
import { Address } from '../Components/Address/Address';
import { DynamicImageUploader } from '../Components/ImageUpload/DynamicImageUploader';
import {
  addressChanged,
  addressLookupValidationError,
  contactOptionChanged,
  OrderFormProps
} from '../Utils';
import { RadioButton, TextArea } from 'components/Shared/Form';
import MessageView from '../Components/MessageView';
import { postSendShippingDamage } from 'services/ApiCustomerSupportFormService';
import { IBasicResponse } from 'models/IBasicResponse';
import { useDispatch } from 'react-redux';
import { GtmCustomerSupportFormSubmit } from 'store/googleTagManager/actions';
import { FormType } from 'models/FormType';
import { CustomerContact } from '../Components/CustomerContact/CustomerContact';

const MSG_MAX_LENGTH = 1000;
const MAX_IMAGE_COUNT = 6;

export const ShippingDamageForm = ({ order, error, returnUrl, onSuccess }: OrderFormProps) => {
  const anchorRef = useRef(NO_ELEMENT);
  const scrollToTopIfNeeded = scrollToTarget(anchorRef);
  const [internalState, internalDispatch] = useReducer(reducer, shippingDamageInitialState);
  const [addressChange, toggleAddressChange] = useToggle(false);
  const [loading, toggleLoading] = useToggle(false);
  const reduxDispatch = useDispatch();

  useEffect(() => {
    if (order) {
      internalDispatch({ type: 'address', value: order?.deliveryAddress });
      internalDispatch({ type: 'email', value: order?.email });
    }
    if (error) {
      internalDispatch({
        type: 'hasError',
        value: true
      });
    }
  }, [error, order]);

  useEffect(() => {
    internalDispatch({ type: 'addressError', value: '' });
  }, [addressChange]);

  if (internalState.hasError) {
    return (
      <MessageView
        title={rm('CustomerPage_Return_Error_Title')}
        message={rm('CustomerPage_Return_Error_Msg')}
        returnUrl={returnUrl}
      />
    );
  }

  if (order?.orderNumber === '' || order?.orderNumber === undefined) {
    return (
      <MessageView
        title={rm('CustomerPage_Empty_Return_Title')}
        message={rm('CustomerPage_Empty_Return_Info')}
        returnUrl={returnUrl}
      />
    );
  }
  const {
    address,
    email,
    products,
    damage,
    packageSlip,
    received,
    solution,
    description,
    contactOption,
    images
  } = internalState;

  const completeInternal = () => {
    toggleLoading();
    if (isValid(internalState, addressChange, internalDispatch)) {
      if (address && email && order) {
        return postSendShippingDamage({
          address,
          email,
          orderNumber: order.orderNumber,
          hash: order.hash,
          products: products.filter(p => p.quantity > 0), // Remove those with quantity 0
          damage,
          packageSlipEntry: packageSlip,
          received,
          solution,
          files: images.map(img => img.imageUrl),
          description,
          customerContactOption: contactOption
        })
          .then(() => {
            toggleLoading();
            onSuccess();
            scrollToTopIfNeeded();
            reduxDispatch(GtmCustomerSupportFormSubmit(FormType.ShippingDamage));
          })
          .catch((response: IBasicResponse) => {
            toggleLoading();
            internalDispatch({ type: 'generalError', value: response.message[0] });
          });
      }
    }
    toggleLoading();
    return Promise.resolve();
  };

  const handleMsgInput = (_event: ChangeEvent<HTMLTextAreaElement>, newValue: string) => {
    internalDispatch({
      type: 'description',
      value: newValue
    });
  };

  return (
    <>
      <h3 className={styles.subHeader}>{rm('customerpage_returns_products_header')}</h3>
      {order && (
        <ExtendedInfo
          order={order}
          displayMode={DisplayMode.Claims}
          returnProductChanged={p => productChanged(p, internalDispatch)}
          className={styles.products}
        />
      )}
      {internalState.productsError && (
        <BlueSectionContainer error={true}>{internalState.productsError}</BlueSectionContainer>
      )}
      <h3 className={styles.subHeader}>{rm('customerpage_returns_address_header')}</h3>
      <BlueSectionContainer error={internalState.addressError}>
        <Address
          address={internalState.address}
          email={internalState.email}
          onChange={(address, email) => addressChanged(address, email, internalDispatch)}
          changeMode={addressChange}
          toggleChange={toggleAddressChange}
          validationError={() => {
            addressLookupValidationError(internalDispatch);
          }}
        />
      </BlueSectionContainer>
      {internalState.addressError && <ErrorContainer>{internalState.addressError}</ErrorContainer>}
      <h3 className={styles.subHeader}>{rm('customerpage_claims_reasonarea_header')}</h3>
      <BlueSectionContainer error={internalState.damageError}>
        <strong>{rm('customerservice_shipping_damage_header')}</strong>
        <RadioBtnContainer>
          {getDamageOptions().map(item => (
            <RadioButton
              key={item.id}
              value={item.value}
              label={item.label}
              name="reasons-radio-button"
              checked={internalState.damage === item.value}
              onChange={val => damageChange(val, internalDispatch)}
            />
          ))}
        </RadioBtnContainer>
      </BlueSectionContainer>
      {internalState.damageError && <ErrorContainer>{internalState.damageError}</ErrorContainer>}

      <BlueSectionContainer error={internalState.packageSlipError}>
        <strong>{rm('customerservice_shipping_entry_header')}</strong>
        <RadioBtnContainer>
          {getPackageSlipEntryOptions().map(item => (
            <RadioButton
              key={item.id}
              value={item.value}
              label={item.label}
              name="reasons-radio-button"
              checked={internalState.packageSlip === item.value}
              onChange={val => packageSlipEntryChange(val, internalDispatch)}
            />
          ))}
        </RadioBtnContainer>
      </BlueSectionContainer>
      {internalState.packageSlipError && (
        <ErrorContainer>{internalState.packageSlipError}</ErrorContainer>
      )}

      <BlueSectionContainer error={internalState.receivedError}>
        <strong>{rm('customerservice_shipping_received_header')}</strong>
        <RadioBtnContainer>
          {getReceivedOptions().map(item => (
            <RadioButton
              key={item.id}
              value={item.value}
              label={item.label}
              name="reasons-radio-button"
              checked={internalState.received === item.value}
              onChange={val => receivedChange(val, internalDispatch)}
            />
          ))}
        </RadioBtnContainer>
      </BlueSectionContainer>
      {internalState.receivedError && (
        <ErrorContainer>{internalState.receivedError}</ErrorContainer>
      )}

      <BlueSectionContainer error={internalState.solutionError}>
        <strong>{rm('customerservice_shipping_solution_header')}</strong>
        <RadioBtnContainer>
          {getSolutionRequestsOptions().map(item => (
            <RadioButton
              key={item.id}
              value={item.value}
              label={item.label}
              name="reasons-radio-button"
              checked={internalState.solution === item.value}
              onChange={val => solutionChange(val, internalDispatch)}
            />
          ))}
        </RadioBtnContainer>
      </BlueSectionContainer>
      {internalState.solutionError && (
        <ErrorContainer>{internalState.solutionError}</ErrorContainer>
      )}

      <BlueSectionContainer error={internalState.descriptionError}>
        <strong>{rm('customerpage_claims_description_header')}</strong>
        <TextArea
          onChange={handleMsgInput}
          textlimit={MSG_MAX_LENGTH}
          value={internalState.description}
        />
        <p id={styles.msgLength}>{`(${internalState.description.length}/${MSG_MAX_LENGTH})`}</p>
      </BlueSectionContainer>
      {internalState.descriptionError && (
        <ErrorContainer>{internalState.descriptionError}</ErrorContainer>
      )}
      <BlueSectionContainer error={internalState.imagesError}>
        <div className={styles.msgHeader}>
          <strong>{rm('customerpage_returns_images_header')}</strong>
          <p>{rm('customerservice_shipping_images_description')}</p>
        </div>
        <DynamicImageUploader
          images={internalState.images}
          maxImageCount={MAX_IMAGE_COUNT}
          dispatch={internalDispatch}
        />
      </BlueSectionContainer>
      {internalState.imagesError && <ErrorContainer>{internalState.imagesError}</ErrorContainer>}
      <CustomerContact
        contact={internalState.contactOption}
        onChange={val => contactOptionChanged(val, internalDispatch)}
        error={internalState.contactOptionError}
      />
      <SubmitContainer>
        <LoaderButton appearance="whitePillPrimary" onClick={completeInternal} loading={loading}>
          {loading ? '' : rm('customerpage_returns_submit')}
        </LoaderButton>
        <Button appearance="whitePill" href={returnUrl}>
          {rm('customerpage_returns_cancel')}
        </Button>
      </SubmitContainer>
      {internalState.generalError && (
        <BlueSectionContainer error={true}>{internalState.generalError}</BlueSectionContainer>
      )}
    </>
  );
};
